import { ScrollView, View } from "@-/com.npmjs.react-native";
import { Gesture, GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import {
  cancelAnimation,
  Easing,
  runOnJS,
  runOnUI,
  useSharedValue,
  withDecay,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { svgDivisor } from "@-/dev.improve.haniverse.lib.svg-geometry";
import useLatest from "@-/com.npmjs.use-latest";
import {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import type { ChinaMapRegionId } from "@-/dev.improve.haniverse.data.china-map";
import {
  anchorChinaMapPinch,
  CHINA_MAP_FIT_PADDING,
  type ChinaMapCamera,
  clamp,
  getChinaMapFlightShares,
  getChinaMapOriginBounds,
  includeChinaMapOrigin,
  stepChinaMapPinch,
} from "#china-map-camera";
import {
  findChinaMapAutoFocusedProvince,
  getChinaMapCityLabelProvince,
  getChinaMapLabels,
  type ChinaMapProvinceView,
} from "#china-map-places";
import type { ChinaMapColors } from "#use-china-map-colors";
import { ChinaMapLoadingSkeleton } from "#china-map-loading-skeleton";
import { ChinaMapRenderer } from "#china-map-renderer";
import type { ChinaMapTravelOverlay } from "#china-map-renderer-props";
import { useVisualActivity } from "@-/dev.improve.haniverse.lib.visual-activity";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeTopExtensionContext,
  ScrollEdgeTopInsetContext,
  useScrollEdgeScrollerRegistration,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";

type Bbox = readonly [number, number, number, number];

/** A request to frame a region. `id` changes on every request so re-framing the same region animates. */
export interface ChinaMapFocusRequest {
  bbox: Bbox;
  id: number;
}

/** Zoom controls owned by the canvas; the Map toolbar's menu drives them. */
export interface ChinaMapCameraControls {
  zoomIn: () => void;
  zoomOut: () => void;
}

const MAX_ZOOM = 60;
const FLY_DURATION_MS = 450;
const ZOOM_DURATION_MS = 250;
/** The map's sea: the viewport's own background, behind the regions. */
const VIEWPORT_CLASS_NAME = "flex-1 overflow-hidden bg-neutral-50 dark:bg-muted";

export function ChinaMapCanvas({
  bottomInset,
  cameraControlsRef,
  colors,
  focusedProvince,
  focusRequest,
  mapHeight,
  mapWidth,
  onTapMapPoint,
  provinces,
  regionId,
  topInset: toolbarTopInset,
  travel,
}: {
  /** Resting height of the sheet over the bottom of the canvas; framing keeps regions above it. */
  bottomInset: number;
  /** Zoom without a touchscreen, from the Map toolbar's menu (pinch zooms on touch). */
  cameraControlsRef?: Ref<ChinaMapCameraControls | null>;
  colors: ChinaMapColors;
  focusedProvince: ChinaMapProvinceView | null;
  focusRequest: ChinaMapFocusRequest | null;
  mapHeight: number;
  mapWidth: number;
  /** A tap at a map point, with the zoom (screen px per map unit) the map was tapped at. */
  onTapMapPoint: (x: number, y: number, scale: number) => void;
  provinces: readonly ChinaMapProvinceView[];
  /** The map drawn, whose silhouette stands in for it until its first frame is on screen. */
  regionId: ChinaMapRegionId;
  /** Height of the floating app toolbar over the top of the canvas; framing keeps regions below it. */
  topInset: number;
  travel: ChinaMapTravelOverlay;
}) {
  const topExtension = useChinaMapCanvasTopExtension();
  const safeAreaTopInset = useContext(ScrollEdgeTopInsetContext);
  const topInset = toolbarTopInset + topExtension + safeAreaTopInset;
  const isVisuallyActive = useVisualActivity();
  const [viewport, setViewport] = useState({ height: 0, width: 0 });
  const ready = viewport.width > 0 && viewport.height > 0;
  const latestReady = useLatest(ready && isVisuallyActive);
  // The renderer mounts once the camera has been framed, so its first frame already shows the map
  // where the skeleton is rather than at the shared values' initial camera.
  const [isFramed, setIsFramed] = useState(false);
  // The skeleton stays under the renderer until the map's first frame is on screen. The renderer
  // draws nothing while its chunk loads or its GPU surface prepares that frame.
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  const showFirstFrame = useCallback(() => setHasFirstFrame(true), []);
  // The renderer unmounts while the map is not drawn (beneath Learn's cards); the silhouette then
  // stands in again, so the map reappears over it rather than over a blank sea.
  if (!isVisuallyActive && hasFirstFrame) {
    setHasFirstFrame(false);
  }
  const fitScale = ready
    ? Math.min(
        (viewport.width - CHINA_MAP_FIT_PADDING * 2) / mapWidth,
        (viewport.height - topInset - bottomInset - CHINA_MAP_FIT_PADDING * 2) / mapHeight,
      )
    : 1;
  const minScale = svgDivisor(fitScale * 0.8);
  const maxScale = svgDivisor(fitScale * MAX_ZOOM);

  // `scale` is screen pixels per map unit; the origin is the map point at the viewport's top-left.
  const scale = useSharedValue(1);
  const originX = useSharedValue(0);
  const originY = useSharedValue(0);
  const pinchAnchor = useSharedValue({ mapX: 0, mapY: 0, pointers: 0, scalePerGestureScale: 1 });
  // From the first finger down until the last lifts, whether a pinch has run. The pinch then owns
  // the camera, so the one-finger pan neither moves nor flings it.
  const touchPinched = useSharedValue(false);
  // Framing and the pan bounds both center on the area between toolbar and card.
  const visibleCenterY = topInset + Math.max(1, viewport.height - topInset - bottomInset) / 2;
  // Where the camera came to rest after the last zoom, pan or flight. Labels are laid out, and the
  // province filling the screen is found, (in React) only when the camera settles, not every frame;
  // in between, the renderers size the names for the live zoom.
  const [settled, setSettled] = useState<ChinaMapCamera>({ originX: 0, originY: 0, scale: 1 });
  const labelScale = settled.scale;

  useEffect(() => {
    if (!isVisuallyActive) {
      return;
    }
    // An interrupted fly-to can leave label sizing behind the camera until this tab resumes.
    setSettled({ originX: originX.get(), originY: originY.get(), scale: scale.get() });
    return () => {
      cancelAnimation(scale);
      cancelAnimation(originX);
      cancelAnimation(originY);
    };
  }, [isVisuallyActive, originX, originY, scale]);

  /**
   * Flies the camera from wherever it is to `to`, then settles there. Runs on the UI thread, so the
   * flight starts from the camera exactly as it is. Scale and origin each run their own timing,
   * eased so that together they follow one path (`getChinaMapFlightShares`): whatever is on screen
   * at both ends, like a tapped province and the view framing it, stays on screen all the way.
   */
  const flyTo = (to: ChinaMapCamera, duration: number) => {
    "worklet";
    // A new timing toward the target of an unfinished one resumes that one's timeline, which
    // would put the three out of step. Stopping them first starts each from where the camera is.
    cancelAnimation(scale);
    cancelAnimation(originX);
    cancelAnimation(originY);
    const fromScale = scale.get();
    if (fromScale === to.scale && originX.get() === to.originX && originY.get() === to.originY) {
      runOnJS(setSettled)(to);
      return;
    }
    const ease = Easing.out(Easing.cubic);
    // A timing already at its target completes at once, so settle only when the last one lands.
    const settle = (finished?: boolean) => {
      "worklet";
      if (
        finished === true &&
        scale.get() === to.scale &&
        originX.get() === to.originX &&
        originY.get() === to.originY
      ) {
        runOnJS(setSettled)(to);
      }
    };
    const scaleEasing = (progress: number) => {
      "worklet";
      return getChinaMapFlightShares(fromScale, to.scale, ease(progress)).scale;
    };
    const originEasing = (progress: number) => {
      "worklet";
      return getChinaMapFlightShares(fromScale, to.scale, ease(progress)).origin;
    };
    scale.set(withTiming(to.scale, { duration, easing: scaleEasing }, settle));
    originX.set(withTiming(to.originX, { duration, easing: originEasing }, settle));
    originY.set(withTiming(to.originY, { duration, easing: originEasing }, settle));
  };

  const frame = (bbox: Bbox, animate: boolean) => {
    const [minX, minY, maxX, maxY] = bbox;
    const visibleHeight = Math.max(1, viewport.height - topInset - bottomInset);
    const toScale = clamp(
      Math.min(
        (viewport.width - CHINA_MAP_FIT_PADDING * 2) / svgDivisor(maxX - minX),
        (visibleHeight - CHINA_MAP_FIT_PADDING * 2) / svgDivisor(maxY - minY),
      ),
      minScale,
      maxScale,
    );
    const toOriginX = (minX + maxX) / 2 - viewport.width / 2 / toScale;
    const toOriginY = (minY + maxY) / 2 - (topInset + visibleHeight / 2) / toScale;
    const to = { originX: toOriginX, originY: toOriginY, scale: toScale };
    if (!animate) {
      scale.set(toScale);
      originX.set(toOriginX);
      originY.set(toOriginY);
      setSettled(to);
      return;
    }
    runOnUI(flyTo)(to, FLY_DURATION_MS);
  };

  const zoomBy = (factor: number) => {
    // The middle of the area between toolbar and sheet stays put.
    const centerX = viewport.width / 2;
    const centerY = topInset + Math.max(1, viewport.height - topInset - bottomInset) / 2;
    runOnUI(() => {
      "worklet";
      const currentScale = svgDivisor(scale.get());
      const toScale = clamp(currentScale * factor, minScale, maxScale);
      flyTo(
        {
          originX: originX.get() + centerX / currentScale - centerX / toScale,
          originY: originY.get() + centerY / currentScale - centerY / toScale,
          scale: toScale,
        },
        ZOOM_DURATION_MS,
      );
    })();
  };
  const latestZoomBy = useLatest(zoomBy);

  useImperativeHandle(
    cameraControlsRef,
    () => ({
      zoomIn: () => latestZoomBy.current(2),
      zoomOut: () => latestZoomBy.current(0.5),
    }),
    [latestZoomBy],
  );

  const latestFrame = useLatest(frame);
  const latestFocusRequest = useLatest(focusRequest);
  // Snap to the current focus (initially the whole country) whenever the viewport is (re)measured.
  useEffect(() => {
    if (ready) {
      latestFrame.current(latestFocusRequest.current?.bbox ?? [0, 0, mapWidth, mapHeight], false);
      setIsFramed(true);
    }
  }, [
    latestFocusRequest,
    latestFrame,
    mapHeight,
    mapWidth,
    ready,
    viewport.height,
    viewport.width,
  ]);
  // Sheet detent changes only reveal/cover the map; they never move the camera.
  // Explicit focus requests frame using the latest sheet height.
  // Fly to each new focus request.
  useEffect(() => {
    if (focusRequest !== null && latestReady.current) {
      latestFrame.current(focusRequest.bbox, true);
    }
  }, [focusRequest, latestFrame, latestReady]);

  // The page's tap handler changes with its selection and travel state (every second in transit).
  // Rebuilding the gesture would cancel a pinch in progress, so the gesture calls a stable wrapper.
  const latestOnTapMapPoint = useLatest(onTapMapPoint);
  const tapMapPoint = useCallback(
    (x: number, y: number, scale: number) => latestOnTapMapPoint.current(x, y, scale),
    [latestOnTapMapPoint],
  );

  const gesture = useMemo(() => {
    const pan = Gesture.Pan()
      .maxPointers(1)
      .onBegin(() => {
        touchPinched.set(false);
      })
      .onStart(() => {
        cancelAnimation(scale);
        cancelAnimation(originX);
        cancelAnimation(originY);
      })
      .onChange((event) => {
        if (touchPinched.get()) {
          return;
        }
        const currentScale = scale.get();
        const currentX = originX.get();
        const currentY = originY.get();
        const boundsX = getChinaMapOriginBounds(currentScale, viewport.width / 2, mapWidth);
        const boundsY = getChinaMapOriginBounds(currentScale, visibleCenterY, mapHeight);
        originX.set(
          clamp(
            currentX - event.changeX / currentScale,
            ...includeChinaMapOrigin(boundsX, currentX),
          ),
        );
        originY.set(
          clamp(
            currentY - event.changeY / currentScale,
            ...includeChinaMapOrigin(boundsY, currentY),
          ),
        );
      })
      .onEnd((event, success) => {
        // A drag that turned into a pinch neither flings nor settles: the pinch settles when it ends.
        if (touchPinched.get()) {
          return;
        }
        const currentScale = scale.get();
        runOnJS(setSettled)({
          originX: originX.get(),
          originY: originY.get(),
          scale: currentScale,
        });
        // Only a drag that ended normally flings. A cancelled one (a second finger landing on
        // Android) must not glide away.
        if (!success) {
          return;
        }
        const boundsX = getChinaMapOriginBounds(currentScale, viewport.width / 2, mapWidth);
        const boundsY = getChinaMapOriginBounds(currentScale, visibleCenterY, mapHeight);
        originX.set(
          withDecay({
            clamp: includeChinaMapOrigin(boundsX, originX.get()),
            velocity: -event.velocityX / currentScale,
          }),
        );
        originY.set(
          withDecay(
            {
              clamp: includeChinaMapOrigin(boundsY, originY.get()),
              velocity: -event.velocityY / currentScale,
            },
            (finished) => {
              // The fling has the same duration on both axes; settle once it comes to rest.
              if (finished === true) {
                runOnJS(setSettled)({
                  originX: originX.get(),
                  originY: originY.get(),
                  scale: scale.get(),
                });
              }
            },
          ),
        );
      });
    const pinch = Gesture.Pinch()
      .onStart((event) => {
        cancelAnimation(scale);
        cancelAnimation(originX);
        cancelAnimation(originY);
        touchPinched.set(true);
        // The map point under the fingers stays under them for the whole pinch.
        pinchAnchor.set(
          anchorChinaMapPinch(
            { originX: originX.get(), originY: originY.get(), scale: scale.get() },
            event,
          ),
        );
      })
      .onUpdate((event) => {
        const next = stepChinaMapPinch(
          { originX: originX.get(), originY: originY.get(), scale: scale.get() },
          pinchAnchor.get(),
          event,
          [minScale, maxScale],
        );
        pinchAnchor.set(next.anchor);
        scale.set(next.camera.scale);
        originX.set(next.camera.originX);
        originY.set(next.camera.originY);
      })
      .onEnd(() => {
        runOnJS(setSettled)({ originX: originX.get(), originY: originY.get(), scale: scale.get() });
      });
    const tap = Gesture.Tap()
      .maxDistance(12)
      .onEnd((event, success) => {
        if (success) {
          const currentScale = svgDivisor(scale.get());
          runOnJS(tapMapPoint)(
            originX.get() + event.x / currentScale,
            originY.get() + event.y / currentScale,
            currentScale,
          );
        }
      });
    // A tap counts only when the touch neither dragged nor pinched. Otherwise a quick pinch, whose
    // centroid barely moves, also ends as a tap at the fingers' midpoint and flies the camera to
    // whichever region (or the sea) is there.
    return Gesture.Exclusive(Gesture.Simultaneous(pan, pinch), tap);
  }, [
    mapHeight,
    mapWidth,
    maxScale,
    minScale,
    originX,
    originY,
    pinchAnchor,
    scale,
    tapMapPoint,
    touchPinched,
    viewport.width,
    visibleCenterY,
  ]);

  // A province filling most of the area between toolbar and sheet names its cities on its own.
  const autoFocusedProvince = useMemo(() => {
    if (!ready) {
      return null;
    }
    const visibleBottom = Math.max(topInset + 1, viewport.height - bottomInset);
    return findChinaMapAutoFocusedProvince(provinces, [
      settled.originX,
      settled.originY + topInset / settled.scale,
      settled.originX + viewport.width / settled.scale,
      settled.originY + visibleBottom / settled.scale,
    ]);
  }, [bottomInset, provinces, ready, settled, topInset, viewport.height, viewport.width]);
  const cityLabelProvince = getChinaMapCityLabelProvince({ autoFocusedProvince, focusedProvince });
  const labels = useMemo(
    () =>
      getChinaMapLabels(provinces, {
        cityLabelProvince,
        scale: labelScale,
        visibleBbox: [
          settled.originX + 4 / labelScale,
          settled.originY + (topInset + 4) / labelScale,
          settled.originX + (viewport.width - 4) / labelScale,
          settled.originY + (viewport.height - bottomInset - 4) / labelScale,
        ],
      }),
    [
      bottomInset,
      cityLabelProvince,
      provinces,
      labelScale,
      settled,
      topInset,
      viewport.height,
      viewport.width,
    ],
  );
  return (
    <ChinaMapCanvasSurface topExtension={topExtension}>
      <GestureDetector gesture={gesture}>
        <View
          className={VIEWPORT_CLASS_NAME}
          onLayout={({ nativeEvent: { layout } }) =>
            setViewport({ height: layout.height, width: layout.width })
          }
          testID="china-map-viewport"
        >
          {hasFirstFrame ? null : (
            <ChinaMapLoadingSkeleton
              bottomInset={bottomInset}
              regionId={regionId}
              topInset={topInset}
            />
          )}
          {ready && isFramed && isVisuallyActive ? (
            <ChinaMapRenderer
              cityLabelProvince={cityLabelProvince}
              colors={colors}
              focusedProvince={focusedProvince}
              labels={labels}
              labelScale={labelScale}
              onFirstFrame={showFirstFrame}
              originX={originX}
              originY={originY}
              provinces={provinces}
              scale={scale}
              travel={travel}
              viewport={viewport}
            />
          ) : null}
        </View>
      </GestureDetector>
    </ChinaMapCanvasSurface>
  );
}

/**
 * The canvas while its map downloads: the same surface as `ChinaMapCanvas`, holding only the
 * map's silhouette where the canvas will first frame it. The loaded canvas keeps that silhouette
 * until its first frame is on screen, so the map appears directly over it.
 */
export function ChinaMapLoadingCanvas({
  bottomInset,
  regionId,
  topInset: toolbarTopInset,
}: {
  /** As `ChinaMapCanvas` will be given: where the sheet rests when the map opens. */
  bottomInset: number;
  regionId: ChinaMapRegionId;
  /** Height of the floating app toolbar over the top of the canvas. */
  topInset: number;
}) {
  const topExtension = useChinaMapCanvasTopExtension();
  const safeAreaTopInset = useContext(ScrollEdgeTopInsetContext);
  return (
    <ChinaMapCanvasSurface topExtension={topExtension}>
      <View className={VIEWPORT_CLASS_NAME} testID="china-map-viewport">
        <ChinaMapLoadingSkeleton
          bottomInset={bottomInset}
          regionId={regionId}
          topInset={toolbarTopInset + topExtension + safeAreaTopInset}
        />
      </View>
    </ChinaMapCanvasSurface>
  );
}

/** How far the canvas runs up beneath the status bar, for the scroll edge effect. */
function useChinaMapCanvasTopExtension() {
  const statusBarInset = useContext(ScrollEdgeTopExtensionContext);
  useScrollEdgeScrollerRegistration(isScrollEdgeEffectSupported);
  return isScrollEdgeEffectSupported ? statusBarInset : 0;
}

function ChinaMapCanvasSurface({
  children,
  topExtension,
}: {
  children: ReactNode;
  topExtension: number;
}) {
  if (!isScrollEdgeEffectSupported) {
    return <View className="flex-1">{children}</View>;
  }

  // UIKit's scroll edge effect needs a UIScrollView beneath the floating toolbar. The canvas
  // fills it without scrolling: the map's own gestures still own every pan, pinch and tap.
  return (
    <ScrollView
      automaticallyAdjustContentInsets={false}
      bounces={false}
      className="flex-1"
      contentContainerClassName="flex-1"
      contentInsetAdjustmentBehavior="never"
      scrollEnabled={false}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      style={{ marginTop: -topExtension }}
    >
      {children}
    </ScrollView>
  );
}
